import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sendOmnichannelMessage,
  sendOmnichannelTemplateMessage,
  markOmnichannelConversationRead,
  deleteOmnichannelMessage,
  connectOmnichannelChannel,
  disconnectOmnichannelChannel,
  connectWhatsAppCoexistence,
} from './request';
import {
  OMNICHANNEL_ACCOUNTS_QUERY_KEY,
  OMNICHANNEL_CONVERSATIONS_QUERY_KEY,
  OMNICHANNEL_MESSAGES_QUERY_KEY,
  OMNICHANNEL_STATUS_QUERY_KEY,
} from '@/api/queries/omnichannel/key';
import {
  assignOmnichannelConnectUrl,
  closeOmnichannelConnectTab,
} from '@/lib/omnichannel/connect-channel';
import type {
  SendMessagePayload,
  SendTemplateMessagePayload,
  ChannelType,
} from '@/types';

export type ConnectChannelVariables = {
  channel: ChannelType;
  popup: Window | null;
};

export const useMarkOmnichannelConversationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      markOmnichannelConversationRead(conversationId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
    },
  });
};

export const useSendMessage = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendMessagePayload) =>
      sendOmnichannelMessage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_MESSAGES_QUERY_KEY],
      });
      onSuccess?.();
    },
  });
};

export const useSendTemplateMessage = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SendTemplateMessagePayload) =>
      sendOmnichannelTemplateMessage(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_MESSAGES_QUERY_KEY],
      });
      onSuccess?.();
    },
  });
};

export const useDeleteMessage = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) => deleteOmnichannelMessage(messageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_MESSAGES_QUERY_KEY],
      });
      onSuccess?.();
    },
  });
};

export const useConnectChannel = (
  onSuccess?: () => void,
  onError?: () => void,
  onRedirectStarted?: () => void,
  onPopupBlocked?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ channel }: ConnectChannelVariables) =>
      connectOmnichannelChannel(channel),
    onSuccess: (response, { popup }) => {
      if (response.data && typeof response.data === 'string') {
        if (popup && !popup.closed) {
          assignOmnichannelConnectUrl(popup, response.data);
          onRedirectStarted?.();
          return;
        }

        closeOmnichannelConnectTab(popup);
        onPopupBlocked?.();
        return;
      }

      closeOmnichannelConnectTab(popup);
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      onSuccess?.();
    },
    onError: (_error, { popup }) => {
      closeOmnichannelConnectTab(popup);
      onError?.();
    },
  });
};

export const useDisconnectChannel = (
  onSuccess?: () => void,
  onError?: () => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channel: ChannelType) => disconnectOmnichannelChannel(channel),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_ACCOUNTS_QUERY_KEY],
      });
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useConnectWhatsAppCoexistence = (
  onSuccess?: () => void,
  onError?: (error: Error) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: connectWhatsAppCoexistence,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_ACCOUNTS_QUERY_KEY],
      });
      onSuccess?.();
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};
